import Papa from "papaparse";
import { useCallback } from "react";
import type { ColumnDefinition, DataRow } from "../../schema/chart";
import { detectColumns } from "./columnHelpers";

// xlsx (~419 KB minified) is loaded lazily — only when the user picks a real
// spreadsheet file. CSV imports go through papaparse without touching xlsx.

const SPREADSHEET_EXTENSIONS = new Set([".xlsx", ".xls", ".numbers"]);

function getExtension(filename: string): string {
    const idx = filename.lastIndexOf(".");
    return idx >= 0 ? filename.slice(idx).toLowerCase() : "";
}

async function parseSpreadsheetBuffer(
    buffer: ArrayBuffer,
): Promise<{ columns: ColumnDefinition[]; rows: DataRow[] } | null> {
    const XLSX = await import("xlsx");
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return null;

    const sheet = workbook.Sheets[sheetName];
    const raw = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 });
    if (!raw.length || !raw[0]?.length) return null;

    const names: string[] = [];
    for (let i = 0; i < raw[0].length; i++) {
        const h = raw[0][i];
        names.push(String(h ?? "").trim() || `Column ${i}`);
    }

    const dataRows: DataRow[] = [];
    for (let r = 1; r < raw.length; r++) {
        const row = raw[r];
        if (!row || row.every((cell) => cell === undefined || cell === null || cell === ""))
            continue;
        const record: DataRow = {};
        for (let c = 0; c < names.length; c++) {
            record[names[c]] = row[c] != null ? String(row[c]) : "";
        }
        dataRows.push(record);
    }

    if (!dataRows.length) return null;

    const columns = detectColumns(names, dataRows);
    return { columns, rows: dataRows };
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export function useSpreadsheetImport(
    onImport: (columns: ColumnDefinition[], rows: DataRow[]) => void,
    onError?: (message: string) => void,
) {
    const handleFile = useCallback(
        (file: File) => {
            if (file.size > MAX_FILE_SIZE) {
                onError?.("File size must be under 10MB");
                return;
            }

            const ext = getExtension(file.name);

            if (SPREADSHEET_EXTENSIONS.has(ext)) {
                const reader = new FileReader();
                // FileReader doesn't observe promise rejections from async
                // handlers, so the dynamic xlsx import + parse runs inside a
                // void IIFE with try/catch and surfaces failures through
                // onError instead of leaking as an unhandled rejection.
                reader.onload = () => {
                    void (async () => {
                        try {
                            const result = await parseSpreadsheetBuffer(
                                reader.result as ArrayBuffer,
                            );
                            if (result) onImport(result.columns, result.rows);
                            else onError?.("Spreadsheet had no readable rows");
                        } catch {
                            onError?.("Failed to import spreadsheet file");
                        }
                    })();
                };
                reader.onerror = () => onError?.("Failed to read spreadsheet file");
                reader.readAsArrayBuffer(file);
            } else {
                // Default: CSV via papaparse (handles .csv and unknown extensions)
                Papa.parse(file, {
                    header: true,
                    skipEmptyLines: true,
                    complete: (results) => {
                        if (!results.data?.length || !results.meta?.fields?.length) return;

                        const names = results.meta.fields;
                        const rawRows = results.data as Record<string, string>[];
                        const columns = detectColumns(names, rawRows);
                        onImport(columns, rawRows);
                    },
                });
            }
        },
        [onImport, onError],
    );

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = "";
        },
        [handleFile],
    );

    return { handleFile, handleInputChange };
}

/** @deprecated Use useSpreadsheetImport instead */
export const useCSVImport = useSpreadsheetImport;
